import { ReactNode, useMemo } from 'react';

import { useIdParam } from '@/react/hooks/useIdParam';
import { EdgeStack } from '@/react/edge/edge-stacks/types';

import { useWizardSteps } from '@@/Stepper/useWizardSteps';
import { PageHeader } from '@@/PageHeader';
import { Alert } from '@@/Alert';
import { ResourceDetailHeaderSkeleton } from '@@/ResourceDetailHeader/ResourceDetailHeaderSkeleton';
import { StickyFooter } from '@@/StickyFooter/StickyFooter';

import { useWorkflow } from '../queries/useWorkflow';
import { useArtifactEdgeStacks } from '../queries/useArtifactEdgeStacks';
import { Workflow } from '../types';
import {
  WizardStep,
  WizardProvider,
  WizardMode,
} from '../wizard/WizardContext';
import { buildWizardSteps } from '../wizard/buildWizardSteps';

import { EditForm } from './EditForm';

export function EditView() {
  const workflowId = useIdParam('workflowId');
  const workflowQuery = useWorkflow(workflowId);
  const workflow = workflowQuery.data;

  const artifacts = workflow?.artifacts ?? [];
  const isEditable =
    artifacts.length > 0 &&
    artifacts.every(
      (artifact) =>
        artifact.type === 'edgeStack' || artifact.type === 'gitopsDeployment'
    );

  // Fetching only for the artifacts that have one keeps a stack-backed or
  // reconciled artifact's id from being read as an EdgeStackID.
  const edgeStackQueries = useArtifactEdgeStacks(
    isEditable ? artifacts.filter((a) => a.type === 'edgeStack') : []
  );
  const edgeStacks = edgeStackQueries
    .map((query) => query.data)
    .filter(isEdgeStack);

  if (workflowQuery.isLoading) {
    return (
      <Layout>
        <ResourceDetailHeaderSkeleton statBlockCount={1} />
      </Layout>
    );
  }

  if (!workflow || workflowQuery.isError) {
    return (
      <Layout>
        <Alert color="error">
          Failed loading workflow:{' '}
          {workflowQuery.error instanceof Error
            ? workflowQuery.error.message
            : 'Unknown error'}
        </Alert>
      </Layout>
    );
  }

  if (!isEditable) {
    return (
      <Layout>
        <Alert color="warn" title="This workflow cannot be edited">
          Editing is only available for workflows deployed as Edge stacks or by
          the reconciliation engine.
        </Alert>
      </Layout>
    );
  }

  if (edgeStackQueries.some((query) => query.isLoading)) {
    return (
      <Layout title={workflow.name}>
        <ResourceDetailHeaderSkeleton statBlockCount={1} />
      </Layout>
    );
  }

  // Prepopulating from a partial baseline would make the diff on save
  // indistinguishable from a deliberate change, so fail closed instead. A
  // reconciled artifact has no Edge stack to load, so none is expected.
  const expectedEdgeStacks = artifacts.filter(
    (artifact) => artifact.type === 'edgeStack'
  ).length;

  if (edgeStacks.length !== expectedEdgeStacks) {
    return (
      <Layout title={workflow.name}>
        <Alert color="error">
          Failed loading the configuration of this workflow&apos;s stacks.
          Reload to try again.
        </Alert>
      </Layout>
    );
  }

  return <EditWizard workflow={workflow} edgeStacks={edgeStacks} />;
}

function EditWizard({
  workflow,
  edgeStacks,
}: {
  workflow: Workflow;
  edgeStacks: Array<EdgeStack>;
}) {
  const artifactCount = workflow.artifacts.length;
  const steps = useMemo(() => buildWizardSteps(artifactCount), [artifactCount]);
  const context = useWizardSteps<WizardStep>({ steps });

  return (
    <Layout title={workflow.name}>
      <WizardProvider
        context={{ ...context, mode: WizardMode.Edit, artifactCount }}
      >
        <EditForm steps={steps} workflow={workflow} edgeStacks={edgeStacks} />
      </WizardProvider>
    </Layout>
  );
}

function Layout({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <>
      <PageHeader
        title={title ? `Edit ${title}` : 'Edit Workflow'}
        breadcrumbs={[
          { label: 'GitOps Workflows', link: 'portainer.gitops.workflows' },
          title ?? 'Edit Workflow',
        ]}
        reload
      />
      <StickyFooter.Container>
        <div className="form-horizontal">
          <div className="mx-4">{children}</div>
        </div>
      </StickyFooter.Container>
    </>
  );
}

function isEdgeStack(
  edgeStack: EdgeStack | null | undefined
): edgeStack is EdgeStack {
  return !!edgeStack;
}
